import { useMemo, useState } from 'react';
import { Button, Card } from '@playerbase/ui';
import type { PlaytestApiKey } from '@playerbase/types';

import { getEdgeFunctionsBaseUrl } from '../../../lib/edge-functions-url.js';
import { downloadSnippet } from './download-snippet.js';
import { SnippetBlock, type SnippetLang } from './snippet-block.js';
import { buildEventGodotSnippet } from './snippet-event-godot.js';
import { buildEventHttpSnippet } from './snippet-event-http.js';
import { buildEventUnitySnippet } from './snippet-event-unity.js';
import { buildEventUnrealSnippet } from './snippet-event-unreal.js';
import { buildEventWebSnippet } from './snippet-event-web.js';

type Lang = 'godot' | 'unity' | 'unreal' | 'web' | 'http';

const LANG_LABEL: Record<Lang, string> = {
  godot: 'Godot (GDScript)',
  unity: 'Unity (C#)',
  unreal: 'Unreal (C++)',
  web: 'Web (JavaScript)',
  http: 'Raw HTTP',
};

const SNIPPET_LANG: Record<Lang, SnippetLang> = {
  godot: 'gdscript',
  unity: 'csharp',
  unreal: 'cpp',
  web: 'javascript',
  http: 'http',
};

const EVENT_FILENAME: Record<Lang, string> = {
  godot: 'player_base_events.gd',
  unity: 'PlayerBaseEvents.cs',
  unreal: 'PlayerBaseEvents.cpp',
  web: 'player-base-events.js',
  http: 'playerbase-event.sh',
};

interface EventFiringCardProps {
  meta: PlaytestApiKey | null;
  // Same plaintext-after-rotate handshake as PairingIntegrationCard. We
  // share the key with that card so a studio sees both snippets stamped
  // with the same live key during the brief reveal window.
  plaintext: string | null;
}

export function EventFiringCard({ meta, plaintext }: EventFiringCardProps): JSX.Element {
  const [lang, setLang] = useState<Lang>('godot');
  const [copied, setCopied] = useState(false);

  const keyForSnippet = useMemo(() => {
    if (plaintext !== null) return plaintext;
    return 'YOUR_PLAYERBASE_API_KEY';
  }, [plaintext]);

  const functionsBaseUrl = useMemo(() => getEdgeFunctionsBaseUrl(), []);

  const snippet = useMemo(() => {
    if (lang === 'godot') return buildEventGodotSnippet(keyForSnippet, functionsBaseUrl);
    if (lang === 'unity') return buildEventUnitySnippet(keyForSnippet, functionsBaseUrl);
    if (lang === 'unreal') return buildEventUnrealSnippet(keyForSnippet, functionsBaseUrl);
    if (lang === 'web') return buildEventWebSnippet(keyForSnippet, functionsBaseUrl);
    return buildEventHttpSnippet(keyForSnippet, functionsBaseUrl);
  }, [lang, keyForSnippet, functionsBaseUrl]);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore - snippet stays visible for hand-selection
    }
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title>Event firing</Card.Title>
        <Card.Description>
          Call this at every moment in your game that matches an SDK event
          name you configured on a playtest flow. The bot only DMs paired
          testers when a playtest is active; calls outside that window are
          accepted and quietly dropped.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        {meta === null ? (
          <p className="text-sm text-text-muted">
            Generate an API key above first - the snippet below will use it
            once it&rsquo;s available.
          </p>
        ) : null}

        <div role="tablist" aria-label="Event snippet language" className="flex gap-2 border-b border-border">
          {(['godot', 'unity', 'unreal', 'web', 'http'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={lang === value}
              onClick={() => setLang(value)}
              className={
                lang === value
                  ? 'border-b-2 border-accent px-3 py-1.5 text-sm text-text'
                  : 'border-b-2 border-transparent px-3 py-1.5 text-sm text-text-muted hover:text-text'
              }
            >
              {LANG_LABEL[value]}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => downloadSnippet(EVENT_FILENAME[lang], snippet)}
          >
            Download {EVENT_FILENAME[lang]}
          </Button>
          <Button variant="ghost" onClick={() => void handleCopy()}>
            {copied ? 'Copied!' : 'Copy snippet'}
          </Button>
        </div>

        <SnippetBlock code={snippet} lang={SNIPPET_LANG[lang]} />
      </Card.Content>
    </Card>
  );
}
